import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  NO_SERVICE_TYPE,
  can,
  type JobListInput,
  type ListInput,
  type JobStageOutcome,
  formatCommercialJobNumber,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { useSession } from '../session';
import { PageRoot, PageHeader, Toolbar } from '../ui/Page';
import { SearchInput } from '../ui/SearchInput';
import { DataTable, dash, type DataTableColumn } from '@/components/ui/data-table';
import { useServerTable } from '../ui/useServerTable';
import { useWorkspaceTable } from '../ui/useWorkspaceTable';
import { Notice, StatusBadge } from '../ui/Badge';
import { Sheet, SheetOpenFull } from '../ui/Sheet';
import { EmptyState } from '../ui/EmptyState';
import {
  ErrorState,
  ForbiddenState,
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { PageSkeleton } from '../ui/Skeleton';
import { Button } from '../ui/Button';
import { NewJobSheet } from '../create/NewJobSheet';
import { IconPlus } from '../icons';
import { UnderlineTabStrip, UnderlineTabs } from '../ui/Tabs';
import { RepeatingWorkPanel } from '../agreements/RepeatingWorkPanel';
import { JOB_STATUS, display } from '../status';
import { IconJobs } from '../icons';
import { ServiceTypeStep } from '../servicetypes/ServiceTypeStep';
import {
  EXISTING_JOB_PICKER,
  SetServiceTypeResult,
  jobName,
  useSetServiceType,
} from '../servicetypes/SetServiceType';
import {
  ServiceTypeFilter,
  deadFilterWatch,
  shownServiceTypeFilter,
  type DeadFilterWatch,
} from '../servicetypes/ServiceTypeFilter';
import {
  JOB_WORKSPACE_VIEWS,
  useUrlParam,
  useUrlServiceType,
  useUrlTab,
  workspaceId,
} from '../ui/pageTabs';
import {
  JobPipelineBoard,
  JobValueBuckets,
  stageTone,
  type CommercialJobRow,
  type JobPipeline,
} from '../jobs/JobPipelineBoard';
import { field } from '../create/Field';
import { money } from '../money';

const TABS = [
  { key: 'jobs', label: 'Jobs' },
  { key: 'repeating', label: 'Repeating work' },
];

export type JobRow = {
  id: string;
  jobNumber: number;
  title: string;
  status: string;
  priority: string;
  customerId: string;
  /** Joined by the server. It used to be looked up from a full download of every
   *  customer in the shop, which rendered an em-dash for anyone past the 500-row cap. */
  customerName: string | null;
  /** The shop's current name for the job's type, joined by the server. Null for a job from
   *  before service types existed: the column shows the data-honesty dash, never a guess. */
  serviceTypeLabel: string | null;
  description: string | null;
  createdAt: string | Date;
};

/**
 * The Jobs list's columns. At module scope, not in the page, so a test can render them: no
 * hook reaches into them. Server mode sorts by each `key` through `JOB_SORT` in
 * `services/job.ts`, so a sortable column here needs its key there.
 */
export const jobColumns: DataTableColumn<JobRow>[] = [
  {
    key: 'job',
    label: 'Job',
    value: (j) => j.jobNumber,
    cell: (j) => (
      <div>
        <p className="font-medium">{j.title}</p>
        <p className="font-mono text-xs text-muted-foreground">#{j.jobNumber}</p>
      </div>
    ),
  },
  {
    key: 'customer',
    label: 'Customer',
    value: (j) => j.customerName,
    cell: (j) => dash(j.customerName),
  },
  {
    key: 'serviceType',
    label: 'Service type',
    value: (j) => j.serviceTypeLabel,
    cell: (j) => dash(j.serviceTypeLabel),
  },
  {
    key: 'status',
    label: 'Status',
    value: (j) => display(JOB_STATUS, j.status).label,
    cell: (j) => {
      const s = display(JOB_STATUS, j.status);
      return <StatusBadge tone={s.tone}>{s.label}</StatusBadge>;
    },
  },
  {
    key: 'priority',
    label: 'Priority',
    value: (j) => j.priority,
    cell: (j) =>
      j.priority === 'normal' ? (
        <span className="text-muted-foreground">Normal</span>
      ) : (
        <StatusBadge tone="warn">{j.priority}</StatusBadge>
      ),
  },
  {
    key: 'created',
    label: 'Created',
    value: (j) => new Date(j.createdAt).getTime(),
    searchValue: (j) => new Date(j.createdAt).toLocaleDateString(),
    cell: (j) => new Date(j.createdAt).toLocaleDateString(),
    align: 'right',
  },
];

/**
 * The Jobs list's read: the table's own query (search, sort, page, column filters), narrowed by
 * the service type filter when there is one ('' is every job). Ticket 08.
 */
export function jobListInputFor(query: ListInput, serviceType: string): JobListInput {
  return serviceType ? { ...query, serviceType } : query;
}

/**
 * What the jobs table says when nothing is left to show. Honest about the filter: "No jobs yet"
 * under "No service type" would tell an office that just classified its last older job that the
 * shop has no jobs. No hooks.
 */
export function JobsEmpty({
  filter,
  types,
  workOrderView = false,
}: {
  filter: string;
  types: readonly { id: string; label: string }[] | undefined;
  workOrderView?: boolean;
}) {
  const noun = workOrderView ? 'work order' : 'job';
  if (filter === NO_SERVICE_TYPE) {
    return (
      <EmptyState
        icon={IconJobs}
        title={`Every ${noun} has a service type`}
        description={`No ${noun} is waiting to be classified. New ${noun}s get their type when they are created.`}
      />
    );
  }
  if (filter) {
    const label = types?.find((type) => type.id === filter)?.label;
    return (
      <EmptyState
        icon={IconJobs}
        title={label ? `No ${label} ${noun}s` : `No ${noun}s of this type`}
        description={`No ${noun} has this service type yet.`}
      />
    );
  }
  return (
    <EmptyState
      icon={IconJobs}
      title={`No ${noun}s yet`}
      description={
        workOrderView
          ? 'Work orders hold confirmed work — schedule visits, track parts, and invoice from them.'
          : 'Jobs are the work you do for a customer — schedule visits, track parts, and invoice from them.'
      }
    />
  );
}

/**
 * A selection remembered with the view it was made on: the search, filter, sort and page that
 * put those rows on screen (`viewKey`, the list's read serialised).
 */
export type ScopedSelection = { viewKey: string; keys: ReadonlySet<string> };

export const NO_SELECTION: ScopedSelection = { viewKey: '', keys: new Set() };

/**
 * The keys that are ticked NOW: made on this view, and on screen (`onScreen`).
 *
 * Ticks belong to rows you can SEE (the Schedule page's law, where changing day clears them).
 * Carried to another page, filter or search, they would leave the office one press from changing
 * jobs that are nowhere on screen. The page drops the selection when its view changes (an
 * effect, so it is gone for good, and page 1 → 2 → 1 brings nothing back); this answers for the
 * render in between, and for a ticked row that has since left the page (a refetch after someone
 * else's change), so the boxes and the press can never disagree for a render.
 */
export function selectedIn(
  selection: ScopedSelection,
  viewKey: string,
  onScreen: readonly string[],
): ReadonlySet<string> {
  if (selection.viewKey !== viewKey) return new Set();
  return new Set(onScreen.filter((key) => selection.keys.has(key)));
}

/**
 * Is "Set service type" on screen? For those who may set, while there is anything to classify:
 * a job on the list, a filter that may have emptied it, or an answer to read. NEVER "while jobs
 * are ticked": a set spends the selection, and a step that vanished with it would drop the
 * pressed button's focus to <body> (LESSONS 2026-09-29). No hooks, so a table test pins it.
 */
export function showsSetStep({
  canSet,
  total,
  filter,
  answered,
}: {
  canSet: boolean;
  /** How many jobs the list's read found, on every page. */
  total: number;
  /** The service type filter ('' is every job). */
  filter: string;
  /** A set has answered, and its answer is on screen. */
  answered: boolean;
}): boolean {
  return canSet && (total > 0 || filter !== '' || answered);
}

/**
 * What "Set service type" still needs before its type: the jobs, until one is ticked. No hooks.
 */
export function jobsStillNeeded(tickedCount: number, noun = 'job'): string[] {
  return tickedCount === 0 ? [`the ${noun}s to change (tick them in the list)`] : [];
}

/**
 * The Jobs list. Office staff also classify jobs here (ops insights 08, spec stories 20–28):
 * filter to "No service type" (or one type), tick jobs, and set their type in one press. What
 * the set changed and skipped is drawn below it (`SetServiceTypeResult`); how the set behaves is
 * `setJobServiceType`'s to say.
 *
 * Ticks follow the Schedule page's bulk selection, with its law: ticks belong to rows you can
 * SEE (`selectedIn`), so the press can only ever send jobs that are on screen, ticked (a page is
 * at most 50 rows, half the set's cap of 100).
 */
export function JobsPage() {
  const role = useSession().role;
  return (role === 'owner' || role === 'office') && can(role, 'commercialJob', 'read') ? (
    <JobsWorkspaceGate />
  ) : (
    <ForbiddenState icon={IconJobs} what="jobs" />
  );
}

/** The activation gate never replaces legacy execution reads with empty commercial data. */
function JobsWorkspaceGate() {
  const trpc = useTRPC();
  const q = useQuery(trpc.jobPipeline.list.queryOptions());
  const state = shownState(q, q.data);
  const [legacyServiceType] = useUrlServiceType();
  if (state !== 'ready')
    return (
      <PageRoot>
        <PageHeader title="Jobs" />
        <UnresolvedState
          state={state}
          what="your job workspace"
          icon={IconJobs}
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      </PageRoot>
    );
  if (!q.data) return <PageSkeleton />;
  if (!q.data.enabled || legacyServiceType) return <LegacyJobsPage />;
  return (
    <>
      <StaleNotice
        show={refreshFailed(q, q.data) || q.isPaused}
        onRetry={() => void q.refetch()}
      />
      <CommercialJobsPage
        pipelines={q.data.pipelines}
        defaultPipelineId={q.data.defaultPipelineId}
        settingsStale={q.isPaused || q.isError}
      />
    </>
  );
}

export const commercialJobColumns: DataTableColumn<CommercialJobRow>[] = [
  {
    key: 'job',
    label: 'Job',
    value: (job) => job.jobNumber,
    cell: (job) => (
      <div>
        <p className="font-medium">{job.title}</p>
        <p className="font-mono text-xs text-muted-foreground">
          {formatCommercialJobNumber(job.jobNumber)}
        </p>
      </div>
    ),
  },
  {
    key: 'customer',
    label: 'Customer',
    value: (job) => job.customerName,
    cell: (job) => dash(job.customerName),
  },
  {
    key: 'stage',
    label: 'Stage',
    value: (job) => job.stageLabel,
    cell: (job) => (
      <StatusBadge tone={stageTone(job.stageColor)}>{job.stageLabel}</StatusBadge>
    ),
  },
  {
    key: 'owner',
    label: 'Owner',
    value: (job) => job.ownerName,
    cell: (job) => dash(job.ownerName),
  },
  {
    key: 'serviceType',
    label: 'Service type',
    value: (job) => job.serviceTypeLabel,
    cell: (job) => dash(job.serviceTypeLabel),
  },
  {
    key: 'openQuoted',
    label: 'Open quoted',
    align: 'right',
    cell: (job) => money(job.openQuotedCents),
    labelTooltip:
      'Current quotes awaiting a customer decision; excludes accepted, superseded, and confirmed scope.',
  },
  {
    key: 'acceptedPending',
    label: 'Accepted · awaiting office',
    align: 'right',
    cell: (job) => money(job.acceptedPendingCents),
    labelTooltip: 'Accepted quote scope that has not been confirmed into a work order.',
  },
  {
    key: 'confirmed',
    label: 'Confirmed work orders',
    align: 'right',
    cell: (job) => money(job.confirmedCents),
    labelTooltip:
      'Confirmed work order value, counted once per work order. Payment remains separate.',
  },
  {
    key: 'canceled',
    label: 'Canceled work orders · historical',
    align: 'right',
    defaultHidden: true,
    cell: (job) => money(job.canceledCents),
    labelTooltip:
      'Historical canceled work order value, excluded from active confirmed scope.',
  },
];

function CommercialJobsPage({
  pipelines,
  defaultPipelineId,
  settingsStale,
}: {
  pipelines: JobPipeline[];
  defaultPipelineId: string | null;
  settingsStale: boolean;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const role = useSession().role;
  const { search, setSearch, query, table, params, setParams } = useWorkspaceTable();
  const [view] = useUrlTab(JOB_WORKSPACE_VIEWS, 'view');
  const setView = (next: string) => setParams({ view: next }, false);
  const [pickedPipeline] = useUrlParam('pipelineId', workspaceId, '');
  const setPipeline = (next: string) => setParams({ pipelineId: next });
  const preferences = useQuery(trpc.userPrefs.jobView.queryOptions());
  const restored = useRef(false);
  const saveView = useMutation({
    ...trpc.userPrefs.setJobView.mutationOptions({
      onSuccess: (next) => qc.setQueryData(trpc.userPrefs.jobView.queryKey(), next),
    }),
    scope: { id: 'job-view-preferences' },
  });
  useEffect(() => {
    if (restored.current || !preferences.data) return;
    restored.current = true;
    const preferredPipeline =
      preferences.data.pipelineId &&
      pipelines.some(
        (pipeline) => pipeline.id === preferences.data.pipelineId && pipeline.active,
      )
        ? preferences.data.pipelineId
        : defaultPipelineId;
    if (!params.view || (!pickedPipeline && preferredPipeline))
      setParams(
        {
          view: params.view ?? preferences.data.view,
          pipelineId: pickedPipeline || preferredPipeline || undefined,
        },
        false,
      );
  }, [
    preferences.data,
    params.view,
    pickedPipeline,
    pipelines,
    defaultPipelineId,
    setParams,
  ]);
  const [ownerUserId] = useUrlParam('ownerUserId', workspaceId, '');
  const setOwner = (next: string) => setParams({ ownerUserId: next });
  const [serviceTypeId] = useUrlParam('serviceTypeId', workspaceId, '');
  const setServiceType = (next: string) => setParams({ serviceTypeId: next });
  const [outcome] = useUrlParam(
    'outcome',
    (value) =>
      value === 'open' || value === 'won' || value === 'lost' ? value : undefined,
    '',
  );
  const setOutcome = (next: string) => setParams({ outcome: next });
  const pipelineId = pickedPipeline || defaultPipelineId || '';
  const pipeline = pipelines.find((value) => value.id === pipelineId && value.active);
  const filters = {
    pipelineId: pipelineId || undefined,
    ownerUserId: ownerUserId || undefined,
    serviceTypeId: serviceTypeId || undefined,
    outcome: (outcome || undefined) as JobStageOutcome | undefined,
  };
  const jobs = useQuery({
    ...trpc.commercialJob.list.queryOptions({ ...query, ...filters }),
    enabled: Boolean(pipeline) && view === 'list',
  });
  const members = useQuery(trpc.org.members.queryOptions());
  const types = useQuery(trpc.serviceType.list.queryOptions());
  const state = shownState(jobs, jobs.data);
  const [preview, setPreview] = useState<CommercialJobRow | null>(null);
  const [creating, setCreating] = useState(false);
  return (
    <PageRoot>
      <PageHeader
        title="Jobs"
        sub="The deal, its quotes, and every confirmed work order in one place."
        right={
          <div className="flex flex-wrap gap-2">
            <Link
              to="/quotes"
              className="inline-flex items-center px-3 text-sm font-semibold hover:underline"
            >
              Quotes
            </Link>
            <Link
              to="/work-orders"
              className="inline-flex items-center px-3 text-sm font-semibold hover:underline"
            >
              Work orders
            </Link>
            {can(role, 'commercialJob', 'create') ? (
              <Button
                onClick={() => setCreating(true)}
                disabled={settingsStale || !pipeline}
              >
                <IconPlus size={15} />
                New job
              </Button>
            ) : null}
          </div>
        }
      />
      <UnderlineTabs
        value={view}
        onValueChange={(next) => {
          restored.current = true;
          setView(next);
          if (next === 'list' || next === 'pipeline') saveView.mutate({ view: next });
        }}
      >
        <UnderlineTabStrip
          tabs={[
            { key: 'list', label: 'List' },
            { key: 'pipeline', label: 'Pipeline' },
            { key: 'repeating', label: 'Repeating work' },
          ]}
        />
      </UnderlineTabs>
      {saveView.error ? (
        <Notice tone="warn">
          Your view is available here, but the preference could not be saved.{' '}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              if (saveView.variables) saveView.mutate(saveView.variables);
            }}
          >
            Retry saving preference
          </Button>
        </Notice>
      ) : null}
      {view === 'repeating' ? (
        <RepeatingWorkPanel />
      ) : (
        <>
          <Toolbar>
            <SearchInput
              placeholder="Search jobs…"
              aria-label="Search jobs"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <select
              className={`${field} max-w-xs`}
              aria-label="Job pipeline"
              value={pipelineId}
              onChange={(event) => {
                restored.current = true;
                setPipeline(event.target.value);
                saveView.mutate({ pipelineId: event.target.value });
              }}
            >
              {!pipeline ? (
                <option value={pipelineId}>Choose an active pipeline…</option>
              ) : null}
              {pipelines
                .filter((value) => value.active)
                .map((value) => (
                  <option key={value.id} value={value.id}>
                    {value.label}
                  </option>
                ))}
            </select>
            <select
              className={`${field} max-w-48`}
              aria-label="Job owner"
              value={ownerUserId}
              onChange={(event) => setOwner(event.target.value)}
            >
              <option value="">All owners</option>
              {(members.data ?? []).map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.name ?? member.email}
                </option>
              ))}
            </select>
            <select
              className={`${field} max-w-48`}
              aria-label="Job service type"
              value={serviceTypeId}
              onChange={(event) => setServiceType(event.target.value)}
            >
              <option value="">All service types</option>
              {(types.data ?? []).map((type) => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </select>
            <select
              className={`${field} max-w-40`}
              aria-label="Job outcome"
              value={outcome}
              onChange={(event) => setOutcome(event.target.value)}
            >
              <option value="">All outcomes</option>
              <option value="open">Open</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
            </select>
          </Toolbar>
          {!pipeline ? (
            <Notice tone="warn">
              Choose an active pipeline above, or configure one in Settings.
            </Notice>
          ) : view === 'pipeline' ? (
            <JobPipelineBoard
              pipeline={pipeline}
              search={query.search}
              filters={query.filters}
              ownerUserId={filters.ownerUserId}
              serviceTypeId={filters.serviceTypeId}
              outcome={filters.outcome}
            />
          ) : (
            <>
              <StaleNotice
                show={refreshFailed(jobs, jobs.data) || jobs.isPaused}
                onRetry={() => void jobs.refetch()}
              />
              {state !== 'ready' ? (
                <UnresolvedState
                  state={state}
                  icon={IconJobs}
                  what="your jobs"
                  error={jobs.error}
                  onRetry={() => void jobs.refetch()}
                />
              ) : jobs.data ? (
                <DataTable
                  tableId="commercial-jobs"
                  columns={commercialJobColumns}
                  rows={jobs.data.rows}
                  rowKey={(job) => job.id}
                  rowLabel={(job) =>
                    `Preview ${formatCommercialJobNumber(job.jobNumber)} ${job.title}`
                  }
                  server={table(jobs.data.total, jobs.isFetching)}
                  search={search}
                  onRowClick={setPreview}
                  empty={
                    <EmptyState
                      icon={IconJobs}
                      title="No jobs match this view"
                      description="Change the filters or create a Job to track the next request."
                    />
                  }
                />
              ) : null}
            </>
          )}
        </>
      )}
      <Sheet
        open={preview !== null}
        onClose={() => setPreview(null)}
        title={preview?.title ?? ''}
        badge={
          preview ? (
            <StatusBadge tone={stageTone(preview.stageColor)}>
              {preview.stageLabel}
            </StatusBadge>
          ) : null
        }
        footer={
          preview ? (
            <SheetOpenFull
              onClick={() =>
                navigate({ to: '/jobs/$jobId', params: { jobId: preview.id } })
              }
            />
          ) : null
        }
      >
        {preview ? (
          <div className="space-y-4">
            <p className="font-mono text-sm">
              {formatCommercialJobNumber(preview.jobNumber)}
            </p>
            <p className="text-sm text-muted-foreground">
              {preview.customerName ?? 'Customer not linked'} ·{' '}
              {preview.ownerName ?? 'Owner unassigned'}
            </p>
            <JobValueBuckets values={preview} />
            <p className="text-xs text-muted-foreground">
              Payment and billing are separate from deal value and confirmation.
            </p>
          </div>
        ) : null}
      </Sheet>
      {creating && pipeline ? (
        <NewJobSheet
          open={creating}
          onClose={() => setCreating(false)}
          commercialPipeline={pipeline}
        />
      ) : null}
    </PageRoot>
  );
}

export function WorkOrdersPage() {
  return <LegacyJobsPage workOrderView />;
}

export function LegacyJobsPage({ workOrderView = false }: { workOrderView?: boolean }) {
  const plural = workOrderView ? 'Work orders' : 'Jobs';
  const noun = workOrderView ? 'work order' : 'job';
  const trpc = useTRPC();
  const navigate = useNavigate();
  // Owner and office: the verb the server's set takes. The route is theirs alone today.
  const canSetType = can(useSession().role, 'job', 'update');
  const serviceTypes = useQuery(trpc.serviceType.list.queryOptions());
  // The filter lives in the address (`/jobs?serviceType=none`), so a link can open the list
  // filtered: the Revenue page's "Classify these jobs" does.
  const [pickedFilter, setPickedFilter] = useUrlServiceType();
  const filter = shownServiceTypeFilter(serviceTypes.data, pickedFilter);
  // A type missing from the list the page holds leaves the address only when the server's
  // answer to a fresh ask confirms it has gone, and the page and the filter are still there
  // (`deadFilterWatch`).
  const deadFilter = useRef<DeadFilterWatch | null>(null);
  deadFilter.current ??= deadFilterWatch();
  useEffect(() => {
    const watch = deadFilter.current!;
    watch.start();
    return () => watch.stop();
  }, []);
  useEffect(() => {
    void deadFilter.current!.see(serviceTypes.data, pickedFilter, {
      refetch: serviceTypes.refetch,
      clear: () => setPickedFilter(''),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the setter is new each render
  }, [serviceTypes.data, pickedFilter]);
  // The filter narrows the result set from outside the table, so it joins the page reset.
  const { search, setSearch, query, table } = useServerTable(undefined, filter);
  const jobsInput = jobListInputFor(query, filter);
  const jobs = useQuery({
    ...trpc.job.list.queryOptions(jobsInput),
    placeholderData: (prev) => prev,
  });
  const [preview, setPreview] = useState<JobRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState('jobs');
  const [selection, setSelection] = useState<ScopedSelection>(NO_SELECTION);
  const setType = useSetServiceType({
    // The selection is spent: the jobs it named have their type now, and any left as they
    // were are listed by name below, with why.
    onSuccess: () => setSelection(NO_SELECTION),
  });
  // The view the rows on screen come from: the ticks made on it are the only ones that count.
  const viewKey = JSON.stringify(jobsInput);
  // A new view (search, filter, sort, page) drops the ticks made on the last one, and the last
  // set's answer or refusal, which were about other rows.
  useEffect(() => {
    setSelection(NO_SELECTION);
    setType.forget();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only a new view forgets
  }, [viewKey]);

  if (jobs.isPending) return <PageSkeleton />;
  if (jobs.isError) {
    return (
      <PageRoot>
        <PageHeader title={plural} />
        <ErrorState
          icon={IconJobs}
          what={`${noun}s`}
          onRetry={() => void jobs.refetch()}
        />
      </PageRoot>
    );
  }

  const rows: JobRow[] = jobs.data.rows;
  const ticked = selectedIn(
    selection,
    viewKey,
    rows.map((j) => j.id),
  );
  const tickedJobs = rows.filter((j) => ticked.has(j.id));
  const classifying = showsSetStep({
    canSet: canSetType,
    total: jobs.data.total,
    filter,
    answered: setType.data !== undefined,
  });

  return (
    <PageRoot>
      <PageHeader
        title={plural}
        right={
          tab === 'jobs' ? (
            <Button onClick={() => setCreating(true)}>
              <IconPlus size={15} />
              New job
            </Button>
          ) : null
        }
      />
      {/* Repeating work lives here rather than in the nav: an agreement is the thing that
          turns into jobs, and the IA gets no new top-level item for it (PRD v2 §4). */}
      <UnderlineTabs value={tab} onValueChange={setTab}>
        <UnderlineTabStrip
          tabs={
            workOrderView
              ? TABS.map((item) =>
                  item.key === 'jobs' ? { ...item, label: plural } : item,
                )
              : TABS
          }
        />
      </UnderlineTabs>
      {tab === 'repeating' ? (
        <RepeatingWorkPanel />
      ) : (
        <>
          <Toolbar>
            <SearchInput
              placeholder={`Search ${noun}s…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={`Search ${noun}s`}
            />
            <ServiceTypeFilter
              value={filter}
              types={serviceTypes.data}
              onChange={setPickedFilter}
            />
          </Toolbar>
          {classifying ? (
            <section
              aria-label="Set service type"
              className="space-y-3 rounded-[var(--radius)] border border-border bg-surface p-4"
            >
              <p className="text-sm font-semibold">
                {tickedJobs.length === 0
                  ? `No ${noun}s selected`
                  : `${tickedJobs.length} ${noun}${tickedJobs.length === 1 ? '' : 's'} selected`}
              </p>
              {/* The refusal, once, where it was asked, and announced (`alert`): focus stays
                  on the press, so a screen reader would not otherwise hear it. */}
              {setType.error ? (
                <Notice tone="danger" alert>
                  {setType.error.message}
                </Notice>
              ) : null}
              <ServiceTypeStep
                value={setType.typeId}
                onChange={setType.pick}
                pending={setType.isPending}
                label="Set service type"
                pendingLabel="Setting…"
                missing={jobsStillNeeded(tickedJobs.length, noun)}
                autoFocus={false}
                picker={EXISTING_JOB_PICKER}
                cancelLabel="Clear selection"
                cancelOff={tickedJobs.length === 0}
                onSubmit={(serviceTypeId) => setType.send(tickedJobs, serviceTypeId)}
                onCancel={() => setSelection(NO_SELECTION)}
              />
            </section>
          ) : null}
          {/* Always there for those who can set, and filled when a set lands: announced
              while focus stays on the press (`SetServiceTypeResult`). */}
          {canSetType ? (
            <SetServiceTypeResult result={setType.data} sent={setType.sent} />
          ) : null}
          <DataTable
            tableId="jobs"
            columns={
              workOrderView
                ? jobColumns.map((column) =>
                    column.key === 'job' ? { ...column, label: 'Work order' } : column,
                  )
                : jobColumns
            }
            rows={rows}
            rowKey={(j) => j.id}
            search={search}
            server={table(jobs.data.total, jobs.isFetching)}
            onRowClick={setPreview}
            // One name for a job on its row and its box: "#12 No heat".
            rowLabel={(j) => `Preview ${noun} ${jobName(j)}`}
            selection={
              canSetType
                ? {
                    ticked,
                    onChange: (next) => setSelection({ viewKey, keys: next }),
                    label: (j) => `Select ${noun} ${jobName(j)}`,
                    pageLabel: `Select every ${noun} on this page`,
                  }
                : undefined
            }
            empty={
              <JobsEmpty
                filter={filter}
                types={serviceTypes.data}
                workOrderView={workOrderView}
              />
            }
          />
          <Sheet
            open={preview !== null}
            onClose={() => setPreview(null)}
            title={preview?.title ?? ''}
            badge={
              preview ? (
                <StatusBadge tone={display(JOB_STATUS, preview.status).tone}>
                  {display(JOB_STATUS, preview.status).label}
                </StatusBadge>
              ) : null
            }
            footer={
              preview ? (
                <SheetOpenFull
                  onClick={() =>
                    workOrderView
                      ? navigate({
                          to: '/work-orders/$workOrderId',
                          params: { workOrderId: preview.id },
                        })
                      : navigate({ to: '/jobs/$jobId', params: { jobId: preview.id } })
                  }
                />
              ) : null
            }
          >
            {preview ? (
              <dl className="space-y-3 text-sm">
                {(
                  [
                    [
                      workOrderView ? 'Work order number' : 'Job number',
                      `#${preview.jobNumber}`,
                    ],
                    ['Customer', preview.customerName],
                    ['Service type', preview.serviceTypeLabel],
                    ['Priority', preview.priority],
                    ['Created', new Date(preview.createdAt).toLocaleDateString()],
                    ['Description', preview.description],
                  ] as const
                ).map(([label, value]) => (
                  <div key={label} className="flex items-baseline justify-between gap-4">
                    <dt className="shrink-0 text-muted-foreground">{label}</dt>
                    <dd className="text-right font-medium">{dash(value)}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </Sheet>
          <NewJobSheet open={creating} onClose={() => setCreating(false)} />
        </>
      )}
    </PageRoot>
  );
}
